import React, { useEffect, useRef, useState } from 'react';
import { AdLandingPages } from './ad-landing-pages';
import {
  type AdminApiClient,
  ApiError,
  type JsonRecord,
  textOf,
  unwrapList,
  unwrapRecord,
} from './api-client';

class HostRequestTimeout extends Error {}
const isDenied = (error: unknown): error is ApiError =>
  error instanceof ApiError && error.status === 403;
const loadError = (error: unknown, fallback: string) =>
  error instanceof HostRequestTimeout ? error.message : fallback;

/** Bound each read even if an adapter ignores AbortSignal; late responses stay inert. */
function requestWithin<T>(
  api: AdminApiClient,
  path: string,
  signal: AbortSignal,
  timeoutMessage: string,
): Promise<T> {
  if (signal.aborted) return Promise.reject(new DOMException('读取已取消', 'AbortError'));
  return new Promise((resolve, reject) => {
    const request = new AbortController();
    let settled = false;
    const finish = (complete: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', cancel);
      complete();
    };
    const cancel = () =>
      finish(() => {
        request.abort();
        reject(new DOMException('读取已取消', 'AbortError'));
      });
    signal.addEventListener('abort', cancel, { once: true });
    const timer = setTimeout(
      () =>
        finish(() => {
          request.abort();
          reject(new HostRequestTimeout(timeoutMessage));
        }),
      12_000,
    );
    try {
      void api.request<T>(path, { signal: request.signal, cache: 'no-store' }).then(
        (value) => finish(() => resolve(value)),
        (error: unknown) => finish(() => reject(error)),
      );
    } catch (error) {
      finish(() => reject(error));
    }
  });
}

/** Uses the existing admin App context, independently of city/product filters. */
export function AdLandingHost({
  api,
  pageId,
  landingId,
  appId,
  onAppChange,
}: {
  api: AdminApiClient;
  pageId: string;
  landingId?: string;
  appId: string;
  onAppChange: (appId: string) => void;
}) {
  const isNew = pageId === 'ADM-L01';
  const isEditor = isNew || Boolean(landingId);
  const changeApp = useRef(onAppChange);
  changeApp.current = onAppChange;
  const [state, setState] = useState<{
    apps: JsonRecord[];
    loading: boolean;
    error?: string;
  }>({ apps: [], loading: true });
  const [revision, setRevision] = useState(0);
  const [sourceResolution, setSourceResolution] = useState<{
    loading: boolean;
    error?: string;
  }>({ loading: false });
  const [capabilities, setCapabilities] = useState<{
    appId: string;
    loading: boolean;
    canManage: boolean;
    canPublish: boolean;
    accessDenied?: boolean;
    error?: string;
  }>({ appId: '', loading: false, canManage: false, canPublish: false });
  useEffect(() => {
    const abort = new AbortController();
    setState({ apps: [], loading: true });
    void requestWithin<unknown>(
      api,
      '/api/admin/m8/security/me',
      abort.signal,
      '读取小程序超时，请重试。',
    ).then(
      (value) => {
        if (abort.signal.aborted) return;
        const profile = unwrapRecord(value);
        const apps = unwrapList(profile.apps).filter(
          (app) => !app.deletedAt && (app.status === undefined || app.status === 'ACTIVE'),
        );
        setState({ apps, loading: false });
      },
      (error: unknown) =>
        !abort.signal.aborted &&
        setState({
          apps: [],
          loading: false,
          error: isDenied(error) ? error.message : loadError(error, '读取小程序失败，请重试。'),
        }),
    );
    return () => abort.abort();
  }, [api, revision]);
  useEffect(() => {
    if (!isNew && !state.loading && !state.error && !appId && !landingId && state.apps.length === 1)
      onAppChange(String(state.apps[0]!.id));
  }, [isNew, state, appId, landingId, onAppChange]);
  const authorizedApp = state.apps.find((app) => app.id === appId);
  useEffect(() => {
    const abort = new AbortController();
    if (!appId || !authorizedApp) {
      setCapabilities({ appId: '', loading: false, canManage: false, canPublish: false });
      return;
    }
    setCapabilities({ appId, loading: true, canManage: false, canPublish: false });
    void requestWithin<{ canView: boolean; canManage: boolean; canPublish: boolean }>(
      api,
      `/api/admin/apps/${encodeURIComponent(appId)}/ad-landings/capabilities`,
      abort.signal,
      '读取操作权限超时，请重试。',
    ).then(
      (value) => {
        if (!abort.signal.aborted)
          setCapabilities({
            appId,
            loading: false,
            canManage: value.canManage === true,
            canPublish: value.canPublish === true,
            ...(value.canView
              ? {}
              : { error: '当前账号没有查看此小程序广告页的权限。', accessDenied: true }),
          });
      },
      (error: unknown) => {
        if (!abort.signal.aborted)
          setCapabilities({
            appId,
            loading: false,
            canManage: false,
            canPublish: false,
            accessDenied: isDenied(error),
            error: isDenied(error) ? error.message : loadError(error, '读取操作权限失败，请重试。'),
          });
      },
    );
    return () => abort.abort();
  }, [api, appId, authorizedApp, revision]);
  useEffect(() => {
    const abort = new AbortController();
    if (!isNew || state.loading || state.error || !state.apps.length || (appId && !authorizedApp)) {
      setSourceResolution({ loading: false });
      return;
    }
    if (appId && (capabilities.appId !== appId || capabilities.loading)) return;
    if (appId && capabilities.canManage && !capabilities.error) {
      setSourceResolution({ loading: false });
      return;
    }
    // Pick storage once before mounting a new form. Its publishing target stays
    // local to the editor, so changing that target never remounts or clears inputs.
    setSourceResolution({ loading: true });
    void (async () => {
      let failure =
        appId && capabilities.error && !capabilities.accessDenied ? capabilities.error : '';
      for (const app of state.apps) {
        const id = String(app.id);
        if (id === appId) continue;
        try {
          const value = await requestWithin<{ canView: boolean; canManage: boolean }>(
            api,
            `/api/admin/apps/${encodeURIComponent(id)}/ad-landings/capabilities`,
            abort.signal,
            '读取操作权限超时，请重试。',
          );
          if (abort.signal.aborted) return;
          if (value.canView === true && value.canManage === true) {
            changeApp.current(id);
            return;
          }
        } catch (error) {
          if (abort.signal.aborted) return;
          if (!isDenied(error)) failure ||= loadError(error, '读取操作权限失败，请重试。');
        }
      }
      if (!abort.signal.aborted)
        setSourceResolution({ loading: false, error: failure || '当前账号没有新建落地页的权限。' });
    })();
    return () => abort.abort();
  }, [api, isNew, appId, authorizedApp, state, capabilities, revision]);
  const navigate = (href: string, scope = appId) => {
    const target = new URL(href, location.origin);
    if (scope) target.searchParams.set('appId', scope);
    else target.searchParams.delete('appId');
    history.pushState({}, '', `${target.pathname}${target.search}${target.hash}`);
    dispatchEvent(new PopStateEvent('popstate'));
  };
  if (state.loading) return <section role="status">正在读取可管理的小程序…</section>;
  if (state.error)
    return (
      <section role="alert">
        {state.error} <button onClick={() => setRevision((n) => n + 1)}>重试</button>
      </section>
    );
  return (
    <section className="ad-landing-host">
      {!isEditor && (
        <div className="ad-landing-scope" aria-label="落地页当前小程序">
          {state.apps.length === 1 && (!appId || authorizedApp) ? (
            <span>
              当前小程序：<strong>{textOf(state.apps[0]!, 'displayName', 'name')}</strong>
            </span>
          ) : (
            <label>
              当前小程序{' '}
              <select
                aria-label="当前小程序"
                value={appId}
                disabled={Boolean(landingId)}
                onChange={(event) => onAppChange(event.target.value)}
              >
                <option value="">请选择小程序</option>
                {state.apps.map((app) => (
                  <option key={String(app.id)} value={String(app.id)}>
                    {textOf(app, 'displayName', 'name')}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
      {!state.apps.length ? (
        <section role="status">
          暂无可管理的小程序，请在系统用户详情中分配小程序和广告页权限。
        </section>
      ) : (appId && !authorizedApp) || (landingId && !appId) ? (
        <section role="alert">
          无法确认此页面所属的小程序。请从“落地页管理”选择有权限的页面后编辑。
          <button onClick={() => navigate('/admin/ad-landings', '')}>返回落地页管理</button>
        </section>
      ) : isNew && sourceResolution.error ? (
        <section role="alert">
          {sourceResolution.error} <button onClick={() => setRevision((n) => n + 1)}>重试</button>
        </section>
      ) : isNew && (!appId || sourceResolution.loading || !capabilities.canManage) ? (
        <section role="status">正在准备编辑器…</section>
      ) : appId && (capabilities.appId !== appId || capabilities.loading) ? (
        <section role="status">正在读取当前小程序的操作权限…</section>
      ) : capabilities.appId === appId && capabilities.error ? (
        <section role="alert">
          {capabilities.error} <button onClick={() => setRevision((n) => n + 1)}>重试</button>
        </section>
      ) : (
        <AdLandingPages
          key={`${appId}:${landingId ?? pageId}`}
          api={api}
          appId={appId}
          appName={textOf(authorizedApp ?? {}, 'displayName', 'name')}
          mode={pageId === 'ADM-L01' ? 'new' : landingId ? 'detail' : 'list'}
          {...(landingId ? { landingId } : {})}
          canManage={capabilities.canManage}
          canPublish={capabilities.canPublish}
          onNavigate={navigate}
        />
      )}
    </section>
  );
}
